import { useMemo } from 'react'
import { RegisteredAccount } from 'pages/WorkspacePage/Team'
import { CognitoIdentityProviderClient, ListUsersCommand, UserType } from '@aws-sdk/client-cognito-identity-provider'
import { useBobsInEnvy } from 'queries/Organizations.queries'
import { useIsAdmin } from 'queries/Auth.queries'
import { Maybe, SimpleMap } from '@vendorflow/types'
import { QueryCacheEnum } from 'constants/ReactQuery.constants'
import { useQuery, UseQueryOptions } from '@tanstack/react-query'

export const cognitoUserPoolId = process.env.VF_COGNITO_USER_POOL_ID || ''

export function configureCognitoIdentityClient() {
  return new CognitoIdentityProviderClient({
    region: 'us-west-2',
    credentials: {
      secretAccessKey: process.env.VF_COGNITO_ACCESS_KEY || '',
      accessKeyId: process.env.VF_COGNITO_ACCESS_ID || '',
    },
  })
}

export function cognitoUsersForEnvyQueryKey(envyId: Maybe<string>) {
  return [QueryCacheEnum.ORGANIZATIONS, envyId, QueryCacheEnum.COGNITO_USERS]
}

function useCognitoUsers(envyId: Maybe<string>, options?: UseQueryOptions) {
  const enabled = options?.enabled !== undefined ? options.enabled : true

  return useQuery({
    queryKey: cognitoUsersForEnvyQueryKey(envyId),
    queryFn: async () => {
      const allCognitoUsers: UserType[] = []
      let paginationToken

      do {
        const data: any = await getCognitoUserPage(paginationToken)
        allCognitoUsers.push(...data.Users)
        paginationToken = data.PaginationToken
      } while (paginationToken)

      return allCognitoUsers
    },
    enabled: Boolean(envyId && enabled),
  })
}

export function useCognitoRegisteredAccounts(envyId: Maybe<string>) {
  const isAdmin = useIsAdmin()
  const { data: cognitoUsers, dataUpdatedAt } = useCognitoUsers(envyId)
  const bobs = useBobsInEnvy(envyId)
  const isLoading = isAdmin.isInitialLoading || bobs.isInitialLoading
  const cognitoUsersByEmail = useMemo(() => {
    return (
      cognitoUsers?.reduce<SimpleMap<UserType>>((acc, cognitoData) => {
        const email = cognitoData.Attributes?.find((attribute) => attribute.Name === 'email')
        if (email?.Value) {
          acc[email.Value] = cognitoData
        }
        return acc
      }, {}) || {}
    )
  }, [isLoading, bobs.dataUpdatedAt, dataUpdatedAt])
  const registeredAccounts: RegisteredAccount[] = useMemo(
    () =>
      bobs.data?.map((member) => {
        const user = cognitoUsersByEmail[member.emails[0]]
        return {
          ...member,
          cognitoEnabled: user?.Enabled,
          isAdmin: Boolean(member.rels?.administers?.some((it) => it.id === envyId)),
        }
      }) || [],
    [isLoading, bobs.dataUpdatedAt, dataUpdatedAt, envyId]
  )

  return { registeredAccounts, cognitoUsers, dataUpdatedAt }
}

async function getCognitoUserPage(paginationToken: string) {
  const client = configureCognitoIdentityClient()
  const command = new ListUsersCommand({
    UserPoolId: cognitoUserPoolId,
    PaginationToken: paginationToken,
  })

  return new Promise((resolve, reject) => {
    client.send(command, (err, data) => {
      if (err) {
        reject(err)
        return
      }
      return resolve(data)
    })
  })
}
